import React, { useRef, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Progress,
  Icon,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import {
  useChunkedUpload,
  type MediaCDNUploadResult,
} from '../../../../media-cdn/hooks/useChunkedUpload';
import type { PortalDocument, DocumentStatus } from '../types';

const PROPERTY_PRIMARY = '#0D7F6E';

const statusBadge: Record<DocumentStatus, { label: string; colorScheme: string }> = {
  REQUESTED: { label: 'Action Required', colorScheme: 'orange' },
  UPLOADED: { label: 'Uploaded', colorScheme: 'blue' },
  APPROVED: { label: 'Approved', colorScheme: 'green' },
  REJECTED: { label: 'Returned — please re-upload', colorScheme: 'red' },
};

interface DocumentRequestCardProps {
  doc: PortalDocument;
  onUpload: (documentId: string, fileUrl: string) => void;
}

export const DocumentRequestCard: React.FC<DocumentRequestCardProps> = ({ doc, onUpload }) => {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localStatus, setLocalStatus] = useState<DocumentStatus>(
    (doc.status as string) === 'AWAITING_CLIENT' ? 'REQUESTED' : doc.status
  );

  const { uploadFile, isUploading, progress } = useChunkedUpload({
    chunkSize: 10 * 1024 * 1024,
    onComplete: (result: MediaCDNUploadResult) => {
      setLocalStatus('UPLOADED');
      onUpload(doc.id, result.url);
      toast({ title: 'Document uploaded', status: 'success', duration: 3000, isClosable: true });
    },
    onError: () => {
      toast({ title: 'Upload failed — please try again', status: 'error', duration: 4000, isClosable: true });
    },
  });

  const handleFile = (file: File) => {
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast({ title: 'Unsupported file type. Use PDF, DOC, or image.', status: 'warning', duration: 3000 });
      return;
    }
    uploadFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const canUpload = localStatus === 'REQUESTED' || localStatus === 'REJECTED';
  const badge = statusBadge[localStatus] ?? statusBadge['REQUESTED'];

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor={localStatus === 'REQUESTED' ? 'orange.300' : localStatus === 'APPROVED' ? 'green.200' : 'gray.200'}
      borderRadius="xl"
      p={4}
      boxShadow="sm"
    >
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" flexWrap="wrap" spacing={2}>
          <VStack align="flex-start" spacing={0} flex={1}>
            <Text fontWeight="semibold" fontSize="sm" color="gray.800">
              {doc.title}
            </Text>
            {doc.description && (
              <Text fontSize="xs" color="gray.500">
                {doc.description}
              </Text>
            )}
          </VStack>
          <Badge colorScheme={badge.colorScheme} borderRadius="full" px={2} py={0.5} fontSize="xs">
            {badge.label}
          </Badge>
        </HStack>

        {doc.dueDate && localStatus !== 'APPROVED' && (
          <Text fontSize="xs" color="orange.500">
            Due {format(parseISO(doc.dueDate), 'd MMM yyyy')}
          </Text>
        )}

        {localStatus === 'REJECTED' && doc.rejectionReason && (
          <HStack
            bg="red.50"
            border="1px solid"
            borderColor="red.200"
            borderRadius="md"
            p={2}
            spacing={2}
          >
            <Icon as={FiAlertCircle} color="red.500" boxSize={4} flexShrink={0} />
            <Text fontSize="xs" color="red.700">
              {doc.rejectionReason}
            </Text>
          </HStack>
        )}

        {localStatus === 'APPROVED' && (
          <HStack spacing={2} color="green.600">
            <Icon as={FiCheckCircle} boxSize={4} />
            <Text fontSize="xs">Document approved</Text>
          </HStack>
        )}

        {canUpload && !isUploading && (
          <Box
            border="2px dashed"
            borderColor={isDragging ? PROPERTY_PRIMARY : 'gray.300'}
            borderRadius="lg"
            bg={isDragging ? 'teal.50' : 'gray.50'}
            p={3}
            textAlign="center"
            cursor="pointer"
            transition="all 0.15s"
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <VStack spacing={1}>
              <Icon as={FiUpload} boxSize={5} color={isDragging ? PROPERTY_PRIMARY : 'gray.400'} />
              <Text fontSize="xs" color="gray.500">
                Drag & drop or <Text as="span" color={PROPERTY_PRIMARY} fontWeight="semibold">browse</Text>
              </Text>
              <Text fontSize="2xs" color="gray.400">PDF · DOC · JPG · PNG</Text>
            </VStack>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </Box>
        )}

        {isUploading && (
          <VStack spacing={1} align="stretch">
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Icon as={FiFile} color={PROPERTY_PRIMARY} boxSize={4} />
                <Text fontSize="xs" color="gray.600">Uploading…</Text>
              </HStack>
              <Text fontSize="xs" color="gray.500">{Math.round(progress.percentComplete)}%</Text>
            </HStack>
            <Progress
              value={progress.percentComplete}
              size="xs"
              colorScheme="teal"
              borderRadius="full"
            />
          </VStack>
        )}

        {localStatus === 'UPLOADED' && (
          <Button
            size="xs"
            variant="ghost"
            colorScheme="teal"
            leftIcon={<FiUpload />}
            onClick={() => { setLocalStatus('REQUESTED'); }}
          >
            Replace file
          </Button>
        )}
      </VStack>
    </Box>
  );
};
